import { useCallback, useMemo } from 'react';
import { useApiClient } from 'hooks/useApiClient';
import { API_ROUTES } from 'utils/constants';
import {
  AddStockRequest,
  AddStockResponse,
  RemoveStockRequest,
  RemoveStockResponse,
  InventoryHistory,
  InventorySource,
} from 'types/inventory';

/**
 * Inventory API service
 * Consolidates all inventory-related API calls in a single location
 */
export function useInventoryService() {
  const apiClient = useApiClient();

  /**
   * Add stock to inventory (always creates accounting transaction)
   * @param productId - Product ID
   * @param request - Add stock request with quantity, sourceCategoryId, and optional fields
   */
  const addStock = useCallback(
    async (
      productId: string,
      request: AddStockRequest,
    ): Promise<AddStockResponse> => {
      return apiClient.post<AddStockResponse>(
        `${API_ROUTES.INVENTORY}/add/${productId}`,
        request as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  /**
   * Remove stock from inventory (always creates accounting transaction, uses FIFO)
   * @param productId - Product ID
   * @param request - Remove stock request with quantity, destinationCategoryId, and optional fields
   */
  const removeStock = useCallback(
    async (
      productId: string,
      request: RemoveStockRequest,
    ): Promise<RemoveStockResponse> => {
      return apiClient.post<RemoveStockResponse>(
        `${API_ROUTES.INVENTORY}/remove/${productId}`,
        request as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  /**
   * Get inventory sources for a company
   */
  const getInventorySources = useCallback(async (): Promise<
    InventorySource[]
  > => {
    return apiClient.get<InventorySource[]>(`${API_ROUTES.INVENTORY}/sources`);
  }, [apiClient]);

  /**
   * Get inventory history for a product
   * @param productId - Product ID
   */
  const getInventoryHistory = useCallback(
    async (productId: string): Promise<InventoryHistory[]> => {
      return apiClient.get<InventoryHistory[]>(
        `${API_ROUTES.INVENTORY}/history/${productId}`,
      );
    },
    [apiClient],
  );

  return useMemo(
    () => ({
      addStock,
      removeStock,
      getInventoryHistory,
      getInventorySources,
    }),
    [addStock, removeStock, getInventoryHistory, getInventorySources],
  );
}
